import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import betslip from '@gin-betting/betslip';
import core from '@gin-betting/core';

import { SidebarView } from 'app-constants';
import { usePrevious } from 'hooks';
import globalMessages from 'messages';
import ui from 'modules/ui';
import { useSetDefaultStake } from '../../hooks';

const {
    actions: { choseBetslipBetId },
} = betslip;
const {
    utils: { getIdsFromBetslipOddId },
} = core;
const {
    actions: { changeSidebarView, openSidebar },
    selectors: { selectIsSidebarOpen },
} = ui;

interface Input {
    count: number;
    betslipOddId: string;
    chosenBetslipBetId: string | null;
}
interface Output {
    isOpen: boolean;
    toggleIsOpen: VoidFn;
    title: string;
    goToBetslip: VoidFn;
}

const useQuickBetslip = ({
    count,
    betslipOddId,
    chosenBetslipBetId,
}: Input): Output => {
    const isHidden = useSelector(selectIsSidebarOpen);
    const isHiddenRef = useRef(isHidden);
    isHiddenRef.current = isHidden;
    const { formatMessage } = useIntl();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const dispatch = useDispatch();
    const { betslipBetId } = getIdsFromBetslipOddId(betslipOddId);
    useSetDefaultStake({ betslipBetId });
    const prevCount = usePrevious(count);

    const goToBetslip = useCallback(() => {
        dispatch(changeSidebarView({ sidebarView: SidebarView.betslip }));
        dispatch(openSidebar());
    }, [dispatch]);

    const title = formatMessage(globalMessages.bettingSlip);

    useEffect(() => {
        if (isHiddenRef.current) return;

        if (chosenBetslipBetId !== betslipBetId) {
            dispatch(choseBetslipBetId({ betslipBetId }));
        }
    }, [dispatch, chosenBetslipBetId, betslipBetId]);

    useEffect(() => {
        if (!isHidden) return;

        setIsOpen(false);
    }, [isHidden]);

    useEffect(() => {
        if (isHidden) return;

        setIsOpen(!prevCount && !!count);

        /* prevCount from usePrevious hook is don't need here because this ref value */
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [count, isHidden]);

    const toggleIsOpen = useCallback(() => {
        setIsOpen(s => !s);
    }, []);

    return { isOpen: isOpen && !isHidden, toggleIsOpen, title, goToBetslip };
};

export default useQuickBetslip;
